import React, { useMemo } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import { useThemeTokens } from '../../hooks/useThemeTokens';
import { CvDemandTrends } from '../../types/consumerVoice';

// Google Trends interest over the last 12 months (0-100, relative) for
// every compared term — product, brand, category — on the explore's
// shared scale, so a product term that scores 0 all year sits visibly
// under the brand and category lines instead of drawing a lone flat line.
// Older analyses only carry the primary term's series. x-charts cannot
// read CSS variables, so the colors are resolved theme hexes.
const SERIES_COLORS = (accent: string) => [accent, '#5B8DEF', '#9C6ADE', '#E0A030', '#4CAF7D'];

export function ConsumerVoiceDemandChart({ trends }: { trends: CvDemandTrends }) {
  const { tokens } = useThemeTokens();
  const lines = useMemo(() => {
    const byTerm = trends.series_by_term && trends.series_by_term.length
      ? trends.series_by_term
      : [{ term: trends.primary || 'Search interest', points: trends.interest_over_time || [] }];
    return byTerm
      .map((s) => ({
        term: s.term,
        points: (s.points || [])
          .map((p) => ({ at: new Date(p.date), value: p.value }))
          .filter((p) => !Number.isNaN(p.at.getTime())),
      }))
      .filter((s) => s.points.length >= 2);
  }, [trends]);
  if (!lines.length) return null;
  const dates = lines[0].points.map((p) => p.at);
  const colors = SERIES_COLORS(tokens.accentText);
  return (
    <LineChart
      height={220}
      xAxis={[
        {
          scaleType: 'time',
          data: dates,
          valueFormatter: (d: Date) => dayjs(d).format('MMM'),
        },
      ]}
      yAxis={[{ min: 0, max: 100 }]}
      series={lines.map((s, i) => ({
        id: s.term,
        label: s.term === trends.headline_term && s.term !== trends.primary ? `${s.term} (momentum)` : s.term,
        color: colors[i % colors.length],
        curve: 'monotoneX' as const,
        showMark: false,
        area: false,
        data: s.points.map((p) => p.value),
      }))}
      margin={{ top: 8, right: 12, bottom: 48, left: 32 }}
      slotProps={{ legend: { hidden: lines.length < 2, position: { vertical: 'bottom', horizontal: 'middle' }, itemMarkHeight: 8, itemMarkWidth: 8, labelStyle: { fontSize: 11 } } }}
    />
  );
}
